import { useEffect, useRef, useState } from "react";
import type { BootstrapResponse } from "../../../api/schemas";
import type { ReferralMintEvidence } from "../contracts/referral-mint";
import type { WalletSessionController, WalletSessionSnapshot } from "../../../wallet/wallet-session";
import { referralDeploymentReason, resolveReferralMintEvidence } from "../wallet/referral-mint-deployment";
import { ContextChangedError } from "../../../wallet/wallet-operation";
import type { ActionContext } from "../../../state/action-context";
import { errorMessage } from "../../../state/model-helpers";

export function useReferralMintEvidence(input: Pick<ActionContext, "contextKey" | "readContextKey"> & {
  bootstrap: BootstrapResponse | null; sourceToken: string | null; walletController: WalletSessionController;
  walletSnapshot: WalletSessionSnapshot; injectedEvidence: ReferralMintEvidence | null; refreshRevision: number;
}) {
  const { contextKey, readContextKey, walletController, injectedEvidence } = input;
  const key = JSON.stringify([contextKey, input.bootstrap?.contracts, input.bootstrap?.capabilities,
    input.bootstrap?.deployment, input.bootstrap?.tokens, input.bootstrap?.environment, input.bootstrap?.forkTestOnly,
    input.bootstrap?.mintState.providerCheckMode, input.sourceToken, input.refreshRevision]);
  const latest = useRef({ key, input }); latest.current = { key, input };
  const generation = useRef(0);
  const [stored, setStored] = useState<{ key: string; evidence: ReferralMintEvidence | null; reason: string | null } | null>(null);
  useEffect(() => {
    const serial = ++generation.current;
    if (injectedEvidence) return;
    const abort = new AbortController();
    const guard = () => {
      abort.signal.throwIfAborted();
      if (serial !== generation.current || latest.current.key !== key || contextKey !== readContextKey()) throw new ContextChangedError();
    };
    const { bootstrap, sourceToken, walletSnapshot } = latest.current.input;
    const reason = referralDeploymentReason(bootstrap, sourceToken);
    const provider = walletController.getProvider();
    const unavailable = reason ?? (!provider || !walletSnapshot.authenticated ? "Connect and sign in before verifying referral mint." :
      walletSnapshot.chainId !== bootstrap?.contracts.mintChainId ? `Switch to chain ${bootstrap?.contracts.mintChainId}, then review referral mint again.` : null);
    if (unavailable || !bootstrap || !sourceToken || !provider) {
      setStored({ key, evidence: null, reason: unavailable });
      return () => abort.abort();
    }
    resolveReferralMintEvidence(provider, bootstrap, sourceToken, guard, { signal: abort.signal }).then(evidence => {
      guard(); setStored({ key, evidence, reason: null });
    }).catch((error: unknown) => {
      if (abort.signal.aborted || serial !== generation.current || latest.current.key !== key || contextKey !== readContextKey()) return;
      setStored({ key, evidence: null, reason: errorMessage(error) });
    });
    return () => abort.abort();
  }, [key, contextKey, readContextKey, walletController, injectedEvidence]);
  if (injectedEvidence) return { evidence: injectedEvidence, reason: null };
  return stored?.key === key ? { evidence: stored.evidence, reason: stored.reason } :
    { evidence: null, reason: "The referral deployment is being verified. Close this review and review again when verification finishes." };
}
